/**
 * Passwordless OTP login. Step 1: enter email → initiate. Step 2: enter the
 * 6-digit code → verify (sets the session cookie). New emails create a client
 * account automatically.
 */

import { useState } from 'react';
import { useBrand } from '../lib/useBrand';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { Button, Card, Input, Muted } from '@/components/ui';

export default function LoginPage() {
  const brand = useBrand();
  const { initiate, verify } = useAuth();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [authRequestId, setAuthRequestId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await initiate(email.trim());
      setAuthRequestId(r.auth_request_id);
      setStep('code');
    } catch (err) {
      setError(err instanceof ApiError ? friendly(err.code) : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  async function confirmCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await verify(authRequestId, code.trim());
      // AuthProvider sets the user; the router swaps to the app.
    } catch (err) {
      setError(err instanceof ApiError ? friendly(err.code) : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  const accountDeleted =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('account_deleted');

  return (
    <div className="app-bg" style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', padding: '1.5rem' }}>
      <div style={{ width: '100%', maxWidth: 380 }} className="animate-fade-in">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem', letterSpacing: '1px', fontWeight: 800, fontSize: '1.4rem' }}>
          <span style={{ color: 'white' }}>{brand.wordmarkLead}</span>{' '}
          <span style={{ color: 'var(--gold)' }}>{brand.wordmarkTail}</span>
        </div>

        {accountDeleted && (
          <div className="card text-sm text-center" style={{ marginBottom: '1rem', color: 'var(--cream-dim)' }}>
            Your account and data have been deleted.
          </div>
        )}

        <Card>
          {step === 'email' ? (
            <form onSubmit={sendCode}>
              <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem' }}>Sign in</h1>
              <p style={{ margin: '0 0 1.25rem' }}>
                <Muted>We'll email you a 6-digit code. No password needed.</Muted>
              </p>
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                aria-label="Email address"
              />
              {error && <ErrorNote>{error}</ErrorNote>}
              <Button type="submit" disabled={busy} style={{ width: '100%', marginTop: '1rem' }}>
                {busy ? 'Sending…' : 'Send code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={confirmCode}>
              <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.15rem' }}>Enter your code</h1>
              <p style={{ margin: '0 0 1.25rem' }}>
                <Muted>Sent to {email}. Expires in 5 minutes.</Muted>
              </p>
              <Input
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                aria-label="6-digit code"
                style={{ textAlign: 'center', letterSpacing: '0.5rem', fontSize: '1.25rem' }}
              />
              {error && <ErrorNote>{error}</ErrorNote>}
              <Button type="submit" disabled={busy} style={{ width: '100%', marginTop: '1rem' }}>
                {busy ? 'Verifying…' : 'Verify & sign in'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep('email');
                  setCode('');
                  setError('');
                }}
                style={{ width: '100%', marginTop: '0.5rem' }}
              >
                Use a different email
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: '0.85rem',
        padding: '0.6rem 0.8rem',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(239, 68, 68, 0.12)',
        color: 'var(--error)',
        border: '1px solid var(--error)',
        fontSize: '0.85rem',
      }}
    >
      {children}
    </div>
  );
}

function friendly(code: string): string {
  switch (code) {
    case 'invalid_email':
      return 'That email looks off — double-check it.';
    case 'rate_limited':
      return 'Too many attempts. Try again in a few minutes.';
    case 'invalid_code':
      return 'That code is incorrect.';
    case 'code_expired':
      return 'That code expired. Request a new one.';
    case 'too_many_attempts':
      return 'Too many tries. Request a new code.';
    case 'code_not_found':
      return 'We couldn’t find that request. Start over.';
    case 'account_disabled':
      return 'This account has been deactivated. Contact your coach for help.';
    default:
      return 'Something went wrong. Please try again.';
  }
}
